import React, { useState, useEffect, useCallback } from 'react';
import { IoCallOutline, IoCopyOutline, IoRefreshOutline, IoCloseCircleOutline, IoLockClosedOutline, IoEyeOutline, IoEyeOffOutline, IoCalendarOutline, IoTrashOutline } from 'react-icons/io5';

/**
 * PhoneAccess — Inline component for the settings popover.
 * Lets users generate/view/revoke their Soul ID + PIN phone access credentials.
 */
function PhoneAccess({ token, api }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [copiedField, setCopiedField] = useState('');
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [editingPin, setEditingPin] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [showPin, setShowPin] = useState(false);

  // Schedule call state
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [schedulePhone, setSchedulePhone] = useState('');
  const [scheduleModel, setScheduleModel] = useState('Echo');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleRecurring, setScheduleRecurring] = useState('');
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState('');

  const twilioNumber = '+1 (516) 981-3101';
  const twilioNumberDisplay = twilioNumber.replace(/[^+\d]/g, '').replace(/(\+1)(\d{3})(\d{3})(\d{4})/, '$1 ($2) $3-$4');

  const fetchStatus = useCallback(async () => {
    if (!token || !api) return;
    setLoading(true);
    try {
      const res = await fetch(`${api}/phone-access/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (e) {
      console.error('Phone access status error:', e);
    } finally {
      setLoading(false);
    }
  }, [token, api]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const generateCode = async () => {
    if (!token || !api) return;
    setActionLoading(true);
    setError('');
    try {
      const res = await fetch(`${api}/phone-access/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchStatus();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to generate code');
      }
    } catch (e) {
      setError('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  const revokeCode = async () => {
    if (!token || !api) return;
    setActionLoading(true);
    setError('');
    try {
      const res = await fetch(`${api}/phone-access/revoke`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchStatus();
        setEditingPin(false);
      }
    } catch (e) {
      setError('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  const regenerateCode = async () => {
    if (!token || !api) return;
    setActionLoading(true);
    setError('');
    try {
      const res = await fetch(`${api}/phone-access/regenerate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchStatus();
        setEditingPin(false);
      }
    } catch (e) {
      setError('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  const saveCustomPin = async () => {
    if (!token || !api) return;
    if (!/^\d{4,6}$/.test(pinInput)) {
      setPinError('PIN must be 4-6 digits');
      return;
    }
    setActionLoading(true);
    setPinError('');
    try {
      const res = await fetch(`${api}/phone-access/set-pin`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pin: pinInput }),
      });
      if (res.ok) {
        await fetchStatus();
        setEditingPin(false);
        setPinInput('');
      } else {
        const data = await res.json();
        setPinError(data.detail || 'Failed to update PIN');
      }
    } catch (e) {
      setPinError('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(''), 1500);
    });
  };

  // ── Schedule call API functions ──

  const fetchScheduledCalls = useCallback(async () => {
    if (!token || !api) return;
    try {
      const res = await fetch(`${api}/phone-access/scheduled-calls`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setScheduledCalls(data.scheduled_calls || []);
      }
    } catch (e) {
      console.error('Fetch scheduled calls error:', e);
    }
  }, [token, api]);

  useEffect(() => { fetchScheduledCalls(); }, [fetchScheduledCalls]);

  const createScheduledCall = async () => {
    if (!token || !api) return;
    setScheduleError('');
    if (!schedulePhone || !/^\+\d{10,15}$/.test(schedulePhone)) {
      setScheduleError('Enter a valid phone number (e.g. +12125551234)');
      return;
    }
    if (!scheduleDate || !scheduleTime) {
      setScheduleError('Pick a date and time');
      return;
    }
    setScheduleLoading(true);
    try {
      const scheduledAt = `${scheduleDate}T${scheduleTime}:00`;
      const res = await fetch(`${api}/phone-access/schedule-call`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: schedulePhone,
          model_name: scheduleModel,
          scheduled_at: scheduledAt,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          recurring: scheduleRecurring || null,
        }),
      });
      if (res.ok) {
        await fetchScheduledCalls();
        setShowScheduleForm(false);
        setSchedulePhone(''); setScheduleDate(''); setScheduleTime(''); setScheduleRecurring('');
      } else {
        const data = await res.json();
        setScheduleError(data.detail || 'Failed to schedule call');
      }
    } catch (e) {
      setScheduleError('Network error');
    } finally {
      setScheduleLoading(false);
    }
  };

  const deleteScheduledCall = async (callId) => {
    if (!token || !api) return;
    try {
      await fetch(`${api}/phone-access/scheduled-calls/${callId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchScheduledCalls();
    } catch (e) {
      console.error('Delete scheduled call error:', e);
    }
  };

  const callNow = async () => {
    setScheduleError('');
    if (!token || !api) {
      setScheduleError('Not authenticated. Please refresh the page.');
      return;
    }
    if (!schedulePhone || !/^\+\d{10,15}$/.test(schedulePhone)) {
      setScheduleError('Enter a valid phone number starting with + (e.g. +12125551234)');
      return;
    }
    setScheduleLoading(true);
    try {
      const res = await fetch(`${api}/phone-access/call-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: schedulePhone, model_name: scheduleModel }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setScheduleError('');
        setSchedulePhone('');
        setShowScheduleForm(false);
      } else {
        setScheduleError(data.detail || data.error || 'Failed to initiate call');
      }
    } catch (e) {
      setScheduleError(`Network error: ${e.message || 'Could not reach server'}`);
    } finally {
      setScheduleLoading(false);
    }
  };

  if (loading && !status) {
    return (
      <div className="mcv-settings-sub">
        <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.6, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#fff' }}>
          Phone Access
        </div>
        <div style={{ fontSize: 11, opacity: 0.5, color: '#fff' }}>Loading...</div>
      </div>
    );
  }

  const hasActiveCode = status?.has_code && status?.is_active;
  const maskedPin = status?.pin ? status.pin.replace(/./g, '\u2022') : '';

  return (
    <div className="mcv-settings-sub">
      <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.6, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#fff' }}>
        <IoCallOutline style={{ marginRight: 4, verticalAlign: 'middle', fontSize: 12 }} />
        Phone Access
      </div>

      {hasActiveCode ? (
        <>
          {/* Quick Dial — one-shot tel: link */}
          {twilioNumber && status.pin && (
            <a
              href={`tel:${twilioNumber.replace(/[^+\d]/g, '')},,,,${status.access_code?.replace('SOUL-', '') || ''},,,${status.pin},,,1`}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '8px 12px', marginBottom: 10, borderRadius: 6,
                background: 'linear-gradient(135deg, rgba(76,175,80,0.18), rgba(76,175,80,0.08))',
                border: '1px solid rgba(76,175,80,0.3)',
                color: '#4caf50', fontSize: 12, fontWeight: 600,
                textDecoration: 'none', cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              title="Dial number with Soul ID and PIN auto-entered"
            >
              <IoCallOutline style={{ fontSize: 15 }} />
              Quick Dial Legacy
            </a>
          )}

          {/* Soul ID */}
          <div style={{ fontSize: 11, opacity: 0.5, marginBottom: 2, color: 'rgba(255,255,255,0.5)' }}>Soul ID</div>
          <div style={{ fontSize: 12, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: 14, letterSpacing: '1px', color: '#e8c77b' }}>
              {status.access_code}
            </span>
            <button
              onClick={() => copyToClipboard(status.access_code, 'code')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: copiedField === 'code' ? '#4caf50' : '#aaa', fontSize: 13, padding: 2,
                display: 'inline-flex', alignItems: 'center',
              }}
              title="Copy Soul ID"
            >
              <IoCopyOutline />
            </button>
            {copiedField === 'code' && <span style={{ fontSize: 10, color: '#4caf50' }}>Copied!</span>}
          </div>

          {/* PIN */}
          <div style={{ fontSize: 11, opacity: 0.5, marginBottom: 2, color: 'rgba(255,255,255,0.5)' }}>PIN</div>
          {editingPin ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={pinInput}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  setPinInput(val);
                  setPinError('');
                }}
                placeholder="4-6 digits"
                style={{
                  width: 70, fontSize: 13, fontFamily: 'monospace', letterSpacing: '2px',
                  padding: '3px 6px', background: 'rgba(255,255,255,0.08)', color: '#e8c77b',
                  border: '1px solid rgba(232,199,123,0.3)', borderRadius: 4, outline: 'none',
                  textAlign: 'center',
                }}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') saveCustomPin(); if (e.key === 'Escape') { setEditingPin(false); setPinInput(''); } }}
              />
              <button
                onClick={saveCustomPin}
                disabled={actionLoading}
                style={{
                  fontSize: 10, padding: '3px 8px', cursor: 'pointer',
                  background: 'rgba(76,175,80,0.15)', color: '#4caf50',
                  border: '1px solid rgba(76,175,80,0.3)', borderRadius: 4,
                }}
              >
                Save
              </button>
              <button
                onClick={() => { setEditingPin(false); setPinInput(''); setPinError(''); }}
                style={{
                  fontSize: 10, padding: '3px 6px', cursor: 'pointer',
                  background: 'none', color: '#aaa', border: 'none',
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ fontSize: 12, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: 14, letterSpacing: '2px', color: '#e8c77b' }}
              >
                {showPin ? status.pin : maskedPin}
              </span>
              <button
                onClick={() => setShowPin(!showPin)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: showPin ? '#e8c77b' : '#aaa', fontSize: 13, padding: 2,
                  display: 'inline-flex', alignItems: 'center', gap: 2,
                }}
                title={showPin ? 'Hide PIN' : 'View PIN'}
              >
                {showPin ? <IoEyeOffOutline /> : <IoEyeOutline />}
                <span style={{ fontSize: 10 }}>{showPin ? 'Hide' : 'View'}</span>
              </button>
              <button
                onClick={() => copyToClipboard(status.pin, 'pin')}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: copiedField === 'pin' ? '#4caf50' : '#aaa', fontSize: 13, padding: 2,
                  display: 'inline-flex', alignItems: 'center',
                }}
                title="Copy PIN"
              >
                <IoCopyOutline />
              </button>
              <button
                onClick={() => { setEditingPin(true); setPinInput(status.pin || ''); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#aaa', fontSize: 11, padding: 2,
                  display: 'inline-flex', alignItems: 'center', gap: 2,
                }}
                title="Change PIN"
              >
                <IoLockClosedOutline /> Change
              </button>
              {copiedField === 'pin' && <span style={{ fontSize: 10, color: '#4caf50' }}>Copied!</span>}
            </div>
          )}
          {pinError && <div style={{ fontSize: 10, color: '#f66', marginBottom: 4 }}>{pinError}</div>}

          {twilioNumber && (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <IoCallOutline style={{ fontSize: 12 }} />
              Call: <span style={{ fontWeight: 600, color: '#e8c77b' }}>{twilioNumberDisplay}</span>
            </div>
          )}

          {status.last_used && (
            <div style={{ fontSize: 10, opacity: 0.4, marginBottom: 6, color: '#fff' }}>
              Last used: {new Date(status.last_used).toLocaleDateString()}
            </div>
          )}

          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={regenerateCode}
              disabled={actionLoading}
              style={{
                fontSize: 10, padding: '3px 8px', cursor: 'pointer',
                background: 'rgba(255,255,255,0.08)', color: '#ccc',
                border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4,
                display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              <IoRefreshOutline /> New Code
            </button>
            <button
              onClick={revokeCode}
              disabled={actionLoading}
              style={{
                fontSize: 10, padding: '3px 8px', cursor: 'pointer',
                background: 'rgba(255,80,80,0.1)', color: '#f66',
                border: '1px solid rgba(255,80,80,0.2)', borderRadius: 4,
                display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              <IoCloseCircleOutline /> Deactivate
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 11, opacity: 0.55, marginBottom: 6, lineHeight: 1.4, color: '#fff' }}>
            Generate a Soul ID and PIN to call Legacy over the phone.
          </div>
          <button
            onClick={generateCode}
            disabled={actionLoading}
            style={{
              fontSize: 11, padding: '5px 12px', cursor: 'pointer',
              background: 'rgba(232,199,123,0.15)', color: '#e8c77b',
              border: '1px solid rgba(232,199,123,0.3)', borderRadius: 5,
              fontWeight: 600,
            }}
          >
            {actionLoading ? 'Generating...' : 'Generate Soul ID'}
          </button>
        </>
      )}

      {error && (
        <div style={{ fontSize: 10, color: '#f66', marginTop: 4 }}>{error}</div>
      )}

      {/* ── Schedule a Call section ── */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 10, paddingTop: 8 }}>
        <div
          style={{ fontSize: 11, fontWeight: 600, opacity: 0.6, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
          onClick={() => setShowScheduleForm(!showScheduleForm)}
        >
          <IoCalendarOutline style={{ fontSize: 12 }} />
          Schedule a Call
          <span style={{ fontSize: 10, opacity: 0.5, marginLeft: 'auto' }}>{showScheduleForm ? '▲' : '▼'}</span>
        </div>

        {showScheduleForm && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8, maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
            {/* Phone number */}
            <input
              type="tel"
              placeholder="+12125551234"
              value={schedulePhone}
              onChange={(e) => setSchedulePhone(e.target.value)}
              style={{
                fontSize: 12, padding: '5px 8px', borderRadius: 4,
                background: 'rgba(255,255,255,0.06)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.12)', outline: 'none',
                fontFamily: 'monospace', width: '100%', boxSizing: 'border-box',
              }}
            />

            {/* Model selection — value stays Echo/Synth for the API; labels are user-facing */}
            <div style={{ display: 'flex', gap: 4 }}>
              {[
                { value: 'Echo', label: 'Legacy' },
                { value: 'Synth', label: 'Story' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setScheduleModel(value)}
                  style={{
                    flex: 1, fontSize: 11, padding: '4px 0', cursor: 'pointer', borderRadius: 4,
                    background: scheduleModel === value ? 'rgba(232,199,123,0.18)' : 'rgba(255,255,255,0.04)',
                    color: scheduleModel === value ? '#e8c77b' : '#999',
                    border: `1px solid ${scheduleModel === value ? 'rgba(232,199,123,0.3)' : 'rgba(255,255,255,0.08)'}`,
                    fontWeight: scheduleModel === value ? 600 : 400,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Date + Time */}
            <div style={{ display: 'flex', gap: 4, maxWidth: '100%' }}>
              <input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                style={{
                  flex: 1, minWidth: 0, fontSize: 11, padding: '4px 6px', borderRadius: 4,
                  background: 'rgba(255,255,255,0.06)', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.12)', outline: 'none',
                  colorScheme: 'dark', boxSizing: 'border-box',
                }}
              />
              <input
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                style={{
                  width: 80, minWidth: 0, fontSize: 11, padding: '4px 6px', borderRadius: 4,
                  background: 'rgba(255,255,255,0.06)', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.12)', outline: 'none',
                  colorScheme: 'dark', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Recurring */}
            <select
              value={scheduleRecurring}
              onChange={(e) => setScheduleRecurring(e.target.value)}
              style={{
                fontSize: 11, padding: '4px 6px', borderRadius: 4,
                background: 'rgba(255,255,255,0.06)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.12)', outline: 'none',
                colorScheme: 'dark', width: '100%', boxSizing: 'border-box',
              }}
            >
              <option value="">One-time call</option>
              <option value="daily">Daily</option>
              <option value="weekdays">Weekdays (Mon–Fri)</option>
              <option value="weekly">Weekly</option>
            </select>

            {scheduleError && <div style={{ fontSize: 10, color: '#f66' }}>{scheduleError}</div>}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={createScheduledCall}
                disabled={scheduleLoading}
                style={{
                  flex: 1, fontSize: 11, padding: '6px 0', cursor: 'pointer', borderRadius: 4,
                  background: 'rgba(76,175,80,0.15)', color: '#4caf50',
                  border: '1px solid rgba(76,175,80,0.3)', fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                <IoCalendarOutline /> {scheduleLoading ? 'Scheduling...' : 'Schedule'}
              </button>
              <button
                onClick={callNow}
                disabled={scheduleLoading}
                style={{
                  flex: 1, fontSize: 11, padding: '6px 0', cursor: 'pointer', borderRadius: 4,
                  background: 'rgba(232,199,123,0.15)', color: '#e8c77b',
                  border: '1px solid rgba(232,199,123,0.3)', fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                <IoCallOutline /> Call Now
              </button>
            </div>
          </div>
        )}

        {/* Scheduled calls list */}
        {scheduledCalls.length > 0 && (
          <div style={{ marginTop: 4 }}>
            {scheduledCalls.map((sc) => {
              const dt = sc.scheduled_at ? new Date(sc.scheduled_at) : null;
              const timeStr = dt ? dt.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
              return (
                <div
                  key={sc.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '4px 6px', marginBottom: 3, borderRadius: 4,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    fontSize: 10, color: '#ccc',
                  }}
                >
                  <span style={{ color: sc.model_name === 'Synth' ? '#bb86fc' : '#4caf50', fontWeight: 600, minWidth: 32 }}>
                    {sc.model_name}
                  </span>
                  <span style={{ flex: 1, opacity: 0.7 }}>
                    {timeStr}
                    {sc.recurring && <span style={{ marginLeft: 4, color: '#e8c77b', fontSize: 9 }}>({sc.recurring})</span>}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontSize: 9, opacity: 0.5 }}>{sc.phone_number}</span>
                  <button
                    onClick={() => deleteScheduledCall(sc.id)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#f66', fontSize: 12, padding: 2, display: 'inline-flex',
                    }}
                    title="Cancel this call"
                  >
                    <IoTrashOutline />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default PhoneAccess;
